import React, { useEffect } from 'react';
import { FaCalendarAlt, FaShieldAlt } from 'react-icons/fa';

const PrivacyPolicy = () => {

  useEffect(() => {
    // ✅ Page Title
    document.title = 'Privacy Policy | Meetify';

    // ✅ Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read Meetify\'s Privacy Policy to understand how we collect, use, and protect your personal data. Your privacy is our priority.');

    // ✅ Meta Robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) metaRobots.setAttribute('content', 'index, follow');

    // ✅ Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://meetify.co.in/PrivacyPolicy';

    // ✅ OG Tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Privacy Policy | Meetify');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', 'Meetify\'s Privacy Policy — how we collect, use, and protect your data.');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://meetify.co.in/PrivacyPolicy');

    // Cleanup on unmount — restore defaults
    return () => {
      document.title = 'Meetify — Smart Meeting Scheduler | Book Meetings Instantly';
      if (metaDesc) metaDesc.setAttribute('content', 'Meetify is a smart meeting scheduling platform. Share your availability, let others book meetings, and sync with Google Calendar — all in one place.');
      if (canonical) canonical.href = 'https://meetify.co.in/';
    };
  }, []);

  return (
    <div style={styles.page}>

      {/* Navbar */}
      <nav style={styles.navbar}>
        <a href="/" style={styles.brand}>
          <FaCalendarAlt style={{ marginRight: 8, color: '#6366f1' }} />
          Meetify
        </a>
        <div style={styles.navLinks}>
          <a href="/login" style={styles.navLink}>Sign In</a>
          <a href="/login" style={styles.getStartedBtn}>Get Started</a>
        </div>
      </nav>

      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroIcon}>
          <FaShieldAlt size={28} color="#fff" />
        </div>
        <h1 style={styles.heroTitle}>Privacy Policy</h1>
        <p style={styles.heroSub}>
          Last updated: {new Date().toLocaleDateString('en-IN')}
        </p>
        <p style={styles.heroDesc}>
          At Meetify, we value your privacy. This page explains what data we collect, how we use it, and your rights.
        </p>
      </div>

      {/* Content */}
      <div style={styles.content}>

        <Section number="1" title="Information We Collect">
          <p>We collect the following types of information:</p>
          <ul>
            <li><strong>Personal Info:</strong> Name, email, phone number, and billing details when you register or subscribe.</li>
            <li><strong>Usage Data:</strong> Browser type, IP address, device info, and pages you visit.</li>
            <li><strong>Meeting Data:</strong> Information you enter while scheduling meetings (participants, topics, times).</li>
          </ul>
        </Section>

        <Section number="2" title="How We Use Your Information">
          <ul>
            <li>To provide and improve the Meetify service</li>
            <li>To process payments and send invoices</li>
            <li>To send account updates and support messages</li>
            <li>To keep our platform secure</li>
          </ul>
          <p style={styles.note}>We do <strong>not</strong> sell or rent your personal data to any third party.</p>
        </Section>

        <Section number="3" title="Cookies">
          <p>We use cookies to:</p>
          <ul>
            <li>Keep you logged in</li>
            <li>Remember your preferences</li>
            <li>Understand how our service is used</li>
          </ul>
          <p>You can disable cookies in your browser, but some features may not work properly.</p>
        </Section>

        <Section number="4" title="Data Security">
          <p>
            We use SSL encryption and secure servers to protect your data. While we follow industry best practices,
            no system is 100% secure — you share data with us at your own risk.
          </p>
        </Section>

        <Section number="5" title="Data Retention">
          <p>
            We keep your data as long as your account is active. You can request account and data deletion anytime
            by emailing us at <a href="mailto:Mandalrishabh60@yahoo.com" style={styles.link}>Mandalrishabh60@yahoo.com</a>.
          </p>
        </Section>

        <Section number="6" title="Third-Party Services">
          <p>
            We use trusted third-party tools (like payment gateways and analytics) that may collect limited data
            under their own privacy policies. These are used only to help operate Meetify.
          </p>
        </Section>

        <Section number="7" title="Your Rights">
          <p>You have the right to:</p>
          <ul>
            <li>Access or correct your personal data</li>
            <li>Withdraw consent at any time</li>
            <li>Request deletion of your data</li>
          </ul>
          <p>Send your requests to <a href="mailto:Mandalrishabh60@yahoo.com" style={styles.link}>Mandalrishabh60@yahoo.com</a>.</p>
        </Section>

        <Section number="8" title="Children's Privacy">
          <p>Meetify is not intended for users under 18. We do not knowingly collect data from minors.</p>
        </Section>

        {/* Contact Box */}
        <div style={styles.contactBox}>
          <h3 style={styles.contactTitle}>Questions? Contact Us</h3>
          <p style={styles.contactDesc}>If you have any questions about this policy, reach out to our team:</p>
          <div style={styles.contactList}>
            <div>
              <strong>Meetify Support</strong><br />
              <a href="mailto:meetify.co.in@gmail.com" style={styles.link}>meetify.co.in@gmail.com</a>
            </div>
            <div>
              <strong>Rishabh Mandal</strong><br />
              <a href="mailto:Mandalrishabh60@yahoo.com" style={styles.link}>Mandalrishabh60@yahoo.com</a>
            </div>
            <div>
              <strong>Nitesh Mandal</strong><br />
              <a href="mailto:Niteshmandal007@yahoo.com" style={styles.link}>Niteshmandal007@yahoo.com</a>
            </div>
          </div>
        </div>

      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.footerBrand}>
            <FaCalendarAlt style={{ marginRight: 6, color: '#6366f1' }} />
            <strong>Meetify</strong>
          </div>
          <p style={styles.footerCopy}>© {new Date().getFullYear()} Meetify. All rights reserved.</p>
        </div>
        <div>
          <a href="/PrivacyPolicy" style={styles.footerLink}>Privacy</a>
          <a href="/TermsAndConditions" style={styles.footerLink}>Terms</a>
          <a href="/helpcenter" style={styles.footerLink}>Contact</a>
        </div>
      </footer>

    </div>
  );
};

const Section = ({ number, title, children }) => (
  <div style={styles.section}>
    <div style={styles.sectionHeader}>
      <span style={styles.sectionNumber}>{number}</span>
      <h2 style={styles.sectionTitle}>{title}</h2>
    </div>
    <div style={styles.sectionBody}>{children}</div>
  </div>
);

const styles = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#1e293b',
    background: '#f8fafc',
    minHeight: '100vh',
  },

  // Navbar
  navbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 40px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  brand: {
    fontWeight: 700,
    fontSize: 22,
    color: '#1e293b',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  navLink: {
    color: '#475569',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
  getStartedBtn: {
    background: '#6366f1',
    color: '#fff',
    padding: '7px 16px',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
  },

  // Hero
  hero: {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#fff',
    textAlign: 'center',
    padding: '56px 20px 48px',
  },
  heroIcon: {
    background: 'rgba(255,255,255,0.15)',
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  heroTitle: {
    fontSize: 36,
    fontWeight: 800,
    margin: '0 0 8px',
  },
  heroSub: {
    fontSize: 13,
    opacity: 0.8,
    margin: '0 0 16px',
  },
  heroDesc: {
    fontSize: 16,
    opacity: 0.9,
    maxWidth: 520,
    margin: '0 auto',
    lineHeight: 1.6,
  },

  // Content
  content: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '40px 20px 60px',
  },

  // Section
  section: {
    background: '#fff',
    borderRadius: 12,
    padding: '28px 32px',
    marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    borderLeft: '4px solid #6366f1',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  sectionNumber: {
    background: '#6366f1',
    color: '#fff',
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
    color: '#1e293b',
  },
  sectionBody: {
    fontSize: 15,
    lineHeight: 1.7,
    color: '#475569',
  },

  note: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#166534',
    marginTop: 12,
  },

  link: {
    color: '#6366f1',
    textDecoration: 'none',
  },

  // Contact Box
  contactBox: {
    background: '#6366f1',
    color: '#fff',
    borderRadius: 12,
    padding: '36px 32px',
    textAlign: 'center',
    marginTop: 32,
  },
  contactTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: '0 0 8px',
  },
  contactDesc: {
    opacity: 0.85,
    marginBottom: 24,
    fontSize: 15,
  },
  contactList: {
    display: 'flex',
    justifyContent: 'center',
    gap: 48,
    flexWrap: 'wrap',
    fontSize: 15,
    lineHeight: 1.8,
  },

  // Footer
  footer: {
    background: '#1e293b',
    color: '#94a3b8',
    padding: '20px 40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  footerInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 16,
  },
  footerCopy: {
    margin: 0,
    fontSize: 13,
  },
  footerLink: {
    color: '#94a3b8',
    textDecoration: 'none',
    marginLeft: 20,
    fontSize: 13,
  },
};

export default PrivacyPolicy;